import React from 'react';
import { Alert } from 'react-bootstrap';
import {
  ContactDetailLink,
  OpportunityConvertToContactButton,
} from './OpportunityConvertToContactButton';

const ALREADY_CONVERTED_MESSAGE =
  'This opportunity has already been converted to a contact.';

function readFieldValue(recordData, fieldName) {
  if (!recordData || !fieldName) return null;
  const normalized = String(fieldName).toLowerCase();
  const highlight = Array.isArray(recordData.highlightSection) ? recordData.highlightSection : [];
  for (const f of highlight) {
    if (f?.fieldName && String(f.fieldName).toLowerCase() === normalized) {
      return f.value;
    }
  }
  for (const section of recordData.details || []) {
    for (const f of section.fields || []) {
      if (f?.fieldName && String(f.fieldName).toLowerCase() === normalized) {
        return f.value;
      }
    }
  }
  for (const f of recordData.otherHiddenFields || []) {
    if (f?.fieldName && String(f.fieldName).toLowerCase() === normalized) {
      return f.value;
    }
  }
  return null;
}

function isContactConvertedFlag(val) {
  return val === true || val === 'true' || val === 't' || val === 1 || val === '1';
}

/** Resolve primary_contact lookup value from detail recordData (id + optional display label). */
function parsePrimaryContactFromValue(raw) {
  if (raw == null || raw === '') return { id: null, name: null };
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    let id = raw.value ?? raw.id ?? null;
    if (id != null && typeof id === 'object') {
      id = id.value ?? id.id ?? null;
    }
    let name = raw.displayValue;
    if (name != null && typeof name === 'object') {
      name = name.displayValue ?? name.value ?? null;
    }
    return {
      id: id != null && String(id).trim() ? String(id).trim() : null,
      name: name != null && String(name).trim() ? String(name).trim() : null,
    };
  }
  const s = String(raw).trim();
  return s ? { id: s, name: null } : { id: null, name: null };
}

/**
 * Detail layout custom tab: convert current opportunity to contact (custom API).
 * Minimum input: recordId (opportunity id). recordData / objectName / onRefresh are optional
 * (layout provides them; action-driven usage can pass only recordId).
 */
export default function OpportunityConvertToContactTab({
  recordData,
  objectName,
  recordId,
  onRefresh,
}) {
  if (!recordId) {
    return (
      <div className="p-3 text-muted small">
        Save the opportunity before converting to a contact.
      </div>
    );
  }

  if (
    objectName != null &&
    String(objectName).trim() !== '' &&
    String(objectName).toLowerCase() !== 'opportunity'
  ) {
    return (
      <div className="p-3 text-muted small">
        This action is only available on opportunities.
      </div>
    );
  }

  const alreadyConverted = recordData
    ? isContactConvertedFlag(readFieldValue(recordData, 'is_contact_converted__x'))
    : false;

  const primaryContactRaw = recordData ? readFieldValue(recordData, 'primary_contact') : null;
  const { id: linkedContactId, name: linkedContactName } =
    parsePrimaryContactFromValue(primaryContactRaw);

  return (
    <div className="p-3">
      {alreadyConverted ? (
        <Alert variant="danger" className="mb-3 py-3 small">
          <span>{ALREADY_CONVERTED_MESSAGE}</span>
          {linkedContactId ? (
            <>
              {' '}
              <ContactDetailLink
                contactId={linkedContactId}
                className="alert-link fw-semibold text-break"
              >
                {linkedContactName || 'View contact'}
              </ContactDetailLink>
            </>
          ) : null}
        </Alert>
      ) : (
        <p className="text-muted small mb-3">
          Create a contact from this opportunity using field mappings configured in the system. The opportunity
          will be marked as contact-converted.
        </p>
      )}
      <OpportunityConvertToContactButton
        opportunityId={recordId}
        alreadyConverted={alreadyConverted}
        onSuccess={(data) => {
          const contactId = data?.contact?.id;
          if (contactId) {
            window.location.href = `/view/contact/${encodeURIComponent(contactId)}`;
            return;
          }
          onRefresh?.();
        }}
        onAlreadyConverted={() => onRefresh?.()}
      />
    </div>
  );
}
